import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, CheckCircle, AlertTriangle, X, Link2, Loader2, ArrowRight, ArrowLeft, Search, PlusCircle } from 'lucide-react';
import api from '../../services/api';
import { OBRAS, STATUS_NF, StatusBadge, fmt } from './insumos.utils';

// ── Step 1: Upload da NF ─────────────────────────────────────────────────────
function StepUpload({ onNFCriada }) {
  const [file, setFile] = useState(null);
  const [pedidoId, setPedidoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const fileRef = useRef();

  const { data: pedidos } = useQuery({
    queryKey: ['insumos-pedidos-pendentes'],
    queryFn: () => api.get('/insumos/pedidos', { params: { status: 'aguardando', limit: 100 } }).then(r => r.data.data),
  });

  async function enviar() {
    if (!file) return setErro('Selecione um arquivo XML ou PDF');
    setLoading(true); setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      if (pedidoId) fd.append('pedido_id', pedidoId);
      const { data } = await api.post('/insumos/nf/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onNFCriada(data.data.id);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao enviar arquivo');
    } finally { setLoading(false); }
  }

  return (
    <div className="max-w-xl mx-auto">
      <h2 className="text-lg font-semibold text-gray-800 mb-6">Passo 1 — Upload da Nota Fiscal</h2>

      {/* Drop area */}
      <div
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
        onClick={() => fileRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
      >
        <input ref={fileRef} type="file" accept=".xml,.pdf" className="hidden" onChange={e => setFile(e.target.files[0])} />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <CheckCircle size={32} className="text-green-500" />
            <div className="text-left">
              <p className="font-medium text-green-700">{file.name}</p>
              <p className="text-xs text-green-600">{(file.size / 1024).toFixed(0)} KB</p>
            </div>
            <button onClick={e => { e.stopPropagation(); setFile(null); }} className="ml-4 text-gray-400 hover:text-red-500">
              <X size={18} />
            </button>
          </div>
        ) : (
          <>
            <Upload size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">Arraste ou clique para selecionar</p>
            <p className="text-sm text-gray-400 mt-1">XML NF-e ou PDF — até 20MB</p>
          </>
        )}
      </div>

      {/* Vincular a pedido */}
      <div className="mt-4">
        <label className="block text-sm text-gray-600 mb-1">Vincular a Pedido de Compra (opcional)</label>
        <select value={pedidoId} onChange={e => setPedidoId(e.target.value)}
          className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Sem pedido vinculado</option>
          {(pedidos || []).map(p => (
            <option key={p.id} value={p.id}>{p.numero_pedido} — {p.fornecedor_nome || 'Sem fornecedor'} ({p.obra})</option>
          ))}
        </select>
      </div>

      {erro && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm flex items-center gap-2"><AlertTriangle size={16} />{erro}</div>}

      <button onClick={enviar} disabled={loading || !file}
        className="w-full mt-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
        {loading ? <><Loader2 size={18} className="animate-spin" /> Enviando e processando...</> : <>Enviar e Processar NF <ArrowRight size={18} /></>}
      </button>
    </div>
  );
}

// ── Step 2: Validação dos itens extraídos ─────────────────────────────────────
function StepValidacao({ nfId, onConfirmado }) {
  const qc = useQueryClient();
  const [obra, setObra] = useState('');
  const [localizacaoId, setLocalizacaoId] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [itens, setItens] = useState([]);
  const [nfInfo, setNfInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  // Materiais para auto-link
  const { data: materiaisData } = useQuery({ queryKey: ['insumos-materiais-all'], queryFn: () => api.get('/insumos/materiais', { params: { limit: 500 } }).then(r => r.data.data) });
  const { data: unidades } = useQuery({ queryKey: ['insumos-units'], queryFn: () => api.get('/insumos/unidades').then(r => r.data.data) });
  const { data: localizacoes } = useQuery({ queryKey: ['insumos-locs', obra], queryFn: () => obra ? api.get('/insumos/localizacoes', { params: { obra } }).then(r => r.data.data) : Promise.resolve([]), enabled: !!obra });

  // Processar NF ao montar
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/insumos/nf/${nfId}/processar`);
        setNfInfo(data.data);
        setItens(data.data.itens || []);
        if (data.aviso) setAviso(data.aviso);
        if (data.data.pedido_id) {
          // Pre-fill obra from pedido
        }
      } catch (e) {
        setErro(e.response?.data?.error || 'Erro ao processar NF');
      } finally { setProcessando(false); }
    })();
  }, [nfId]);

  function setItem(index, field, value) {
    setItens(its => its.map((it, i) => i === index ? { ...it, [field]: value } : it));
  }

  // Cadastro rápido: cria material no catálogo com os dados extraídos da NF
  const [cadastrando, setCadastrando] = useState(null); // index do item ou 'todos'
  async function cadastrarItem(idx) {
    const item = itens[idx];
    if (!item?.id || item.material_id) return;
    const { data } = await api.post(`/insumos/nf/${nfId}/itens/${item.id}/criar-material`, { nome: item.descricao });
    setItem(idx, 'material_id', data.data.id);
    return data.data;
  }
  async function cadastrarUm(idx) {
    setCadastrando(idx); setErro('');
    try {
      await cadastrarItem(idx);
      qc.invalidateQueries(['insumos-materiais-all']);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao cadastrar material');
    } finally { setCadastrando(null); }
  }
  async function cadastrarTodos() {
    setCadastrando('todos'); setErro('');
    try {
      for (let i = 0; i < itens.length; i++) {
        if (!itens[i].material_id) await cadastrarItem(i);
      }
      qc.invalidateQueries(['insumos-materiais-all']);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao cadastrar materiais');
    } finally { setCadastrando(null); }
  }

  async function confirmar() {
    if (!obra) return setErro('Selecione a obra de destino');
    const itensMapeados = itens.filter(i => i.material_id);
    if (!itensMapeados.length) return setErro('Vincule ao menos um item a um material do catálogo');
    setLoading(true); setErro('');
    try {
      await api.post(`/insumos/nf/${nfId}/confirmar`, { obra, localizacao_id: localizacaoId || null, responsavel, itens_validados: itens });
      onConfirmado();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao confirmar entrada');
    } finally { setLoading(false); }
  }

  if (processando) return (
    <div className="flex flex-col items-center py-16 gap-4">
      <Loader2 size={40} className="animate-spin text-blue-500" />
      <p className="text-gray-500">Processando NF — extraindo dados...</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-800">Passo 2 — Validar Dados Extraídos</h2>

      {aviso && <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl text-sm flex items-start gap-2"><AlertTriangle size={16} className="mt-0.5 shrink-0" />{aviso}</div>}

      {/* Dados da NF */}
      {nfInfo && (
        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><p className="text-xs text-gray-400">NF Nº</p><p className="font-medium">{nfInfo.numero_nf || '—'}</p></div>
          <div>
            <p className="text-xs text-gray-400">Fornecedor</p>
            <p className="font-medium truncate">{nfInfo.fornecedor_nome || nfInfo.fornecedor_cnpj || '—'}</p>
            {nfInfo.fornecedor_novo && <p className="text-xs text-green-600">✓ cadastrado automaticamente</p>}
          </div>
          <div><p className="text-xs text-gray-400">Emissão</p><p className="font-medium">{nfInfo.data_emissao || '—'}</p></div>
          <div><p className="text-xs text-gray-400">Valor Total</p><p className="font-medium">R$ {nfInfo.valor_total ? parseFloat(nfInfo.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '—'}</p></div>
        </div>
      )}

      {/* Destino */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border rounded-xl p-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Obra de Destino *</label>
          <select value={obra} onChange={e => { setObra(e.target.value); setLocalizacaoId(''); }}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="">Selecionar obra...</option>
            {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Localização Física</label>
          <select value={localizacaoId} onChange={e => setLocalizacaoId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" disabled={!obra}>
            <option value="">Sem localização específica</option>
            {(localizacoes || []).map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Responsável pelo recebimento</label>
          <input value={responsavel} onChange={e => setResponsavel(e.target.value)} placeholder="Nome do responsável"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
      </div>

      {/* Tabela de itens extraídos */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Itens Extraídos da NF ({itens.length})</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {itens.filter(i => i.material_id).length}/{itens.length} vinculados ao catálogo
            </span>
            {itens.some(i => !i.material_id) && (
              <button onClick={cadastrarTodos} disabled={cadastrando !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                {cadastrando === 'todos'
                  ? <><Loader2 size={13} className="animate-spin" /> Cadastrando...</>
                  : <><PlusCircle size={13} /> Cadastrar todos no catálogo</>}
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 border-b">
              <tr>
                <th className="px-3 py-2 text-left">Código NF</th>
                <th className="px-3 py-2 text-left min-w-48">Descrição</th>
                <th className="px-3 py-2 text-right">Qtd</th>
                <th className="px-3 py-2 text-left">Un</th>
                <th className="px-3 py-2 text-right">Preço Unit.</th>
                <th className="px-3 py-2 text-left min-w-52">Material (Catálogo) *</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => (
                <tr key={idx} className={`border-b border-gray-50 ${item.material_id ? 'bg-green-50/30' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{item.codigo_produto || '—'}</td>
                  <td className="px-3 py-2">
                    <input value={item.descricao} onChange={e => setItem(idx, 'descricao', e.target.value)}
                      className="w-full border-0 bg-transparent focus:bg-white focus:border focus:rounded px-1 py-0.5 text-sm" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" value={item.quantidade} onChange={e => setItem(idx, 'quantidade', e.target.value)}
                      className="w-20 text-right border-0 bg-transparent focus:bg-white focus:border focus:rounded px-1 py-0.5 text-sm font-mono" />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{item.unidade_str || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmt(item.preco_unitario)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {item.material_id && <Link2 size={12} className="text-green-500 shrink-0" />}
                      <select value={item.material_id || ''} onChange={e => setItem(idx, 'material_id', e.target.value || null)}
                        className={`w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 ${item.material_id ? 'border-green-300' : 'border-red-200'}`}>
                        <option value="">⚠ Não vinculado (será ignorado)</option>
                        {(materiaisData || []).map(m => (
                          <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>
                        ))}
                      </select>
                      {!item.material_id && item.id && (
                        <button onClick={() => cadastrarUm(idx)} disabled={cadastrando !== null}
                          title="Cadastrar este produto no catálogo com os dados da NF"
                          className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded text-xs font-medium hover:bg-blue-100 disabled:opacity-50 shrink-0 whitespace-nowrap">
                          {cadastrando === idx ? <Loader2 size={12} className="animate-spin" /> : <PlusCircle size={12} />}
                          Cadastrar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!itens.length && (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">Nenhum item extraído da NF</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {erro && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm flex items-center gap-2"><AlertTriangle size={16} />{erro}</div>}

      <div className="flex justify-end">
        <button onClick={confirmar} disabled={loading || !obra}
          className="px-8 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Confirmando...</> : <><CheckCircle size={18} /> Confirmar Entrada no Estoque</>}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Sucesso ───────────────────────────────────────────────────────────
function StepSucesso({ onNova }) {
  return (
    <div className="flex flex-col items-center py-16 gap-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle size={40} className="text-green-500" />
      </div>
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Entrada confirmada!</h2>
        <p className="text-gray-500">O estoque foi atualizado com sucesso.</p>
      </div>
      <div className="flex gap-3">
        <button onClick={onNova} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
          Nova Entrada
        </button>
        <a href="/insumos/materiais" className="px-6 py-2.5 border rounded-xl font-medium text-gray-600 hover:bg-gray-50">
          Ver Catálogo
        </a>
      </div>
    </div>
  );
}

// ── Listagem de NFs recentes ──────────────────────────────────────────────────
function NFsRecentes() {
  const { data } = useQuery({
    queryKey: ['insumos-nfs-recentes'],
    queryFn: () => api.get('/insumos/nf', { params: { limit: 10 } }).then(r => r.data),
  });
  const nfs = data?.data || [];

  if (!nfs.length) return null;

  return (
    <div className="mt-8 bg-white rounded-xl border">
      <div className="px-4 py-3 border-b">
        <h3 className="text-sm font-semibold text-gray-700">NFs Recentes</h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500 border-b">
          <tr>
            <th className="px-4 py-2 text-left">NF Nº</th>
            <th className="px-4 py-2 text-left">Fornecedor</th>
            <th className="px-4 py-2 text-left">Tipo</th>
            <th className="px-4 py-2 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {nfs.map(nf => (
            <tr key={nf.id} className="border-b border-gray-50">
              <td className="px-4 py-2 font-mono">{nf.numero_nf || `NF #${nf.id}`}</td>
              <td className="px-4 py-2 truncate max-w-40">{nf.fornecedor_nome || nf.fornecedor_nome_cadastrado || '—'}</td>
              <td className="px-4 py-2 uppercase text-xs font-mono">{nf.arquivo_tipo}</td>
              <td className="px-4 py-2 text-center"><StatusBadge value={nf.status} map={STATUS_NF} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function InsumosEntrada() {
  const [step, setStep] = useState(1);
  const [nfId, setNfId] = useState(null);

  const steps = [
    { n: 1, label: 'Upload NF' },
    { n: 2, label: 'Validar Dados' },
    { n: 3, label: 'Concluído' },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <FileText size={22} className="text-green-600" />
        <h1 className="text-xl font-bold text-gray-800">Entrada de Estoque — Nota Fiscal</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-0 mb-10">
        {steps.map((s, idx) => (
          <React.Fragment key={s.n}>
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${step === s.n ? 'bg-blue-600 border-blue-600 text-white' : step > s.n ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-400'}`}>
                {step > s.n ? <CheckCircle size={18} /> : s.n}
              </div>
              <span className={`text-xs mt-1 ${step >= s.n ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>{s.label}</span>
            </div>
            {idx < steps.length - 1 && <div className={`h-0.5 w-16 mb-4 mx-1 transition-colors ${step > s.n ? 'bg-green-400' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <StepUpload onNFCriada={(id) => { setNfId(id); setStep(2); }} />
      )}
      {step === 2 && nfId && (
        <StepValidacao nfId={nfId} onConfirmado={() => setStep(3)} />
      )}
      {step === 3 && (
        <StepSucesso onNova={() => { setStep(1); setNfId(null); }} />
      )}

      {step === 1 && <NFsRecentes />}
    </div>
  );
}
